import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/router";
import * as yup from "yup";

import { analytics } from "@flare/analytics";
import { Button } from "@flare/ui/components";

import { getAddress } from "@/components/Funnel/zip-code/zip-code.utils";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { i18n } from "@/services/locale/i18n";
import { setParsedItem } from "@/services/storage";

import type { FunnelAnswers } from "../v2/funnel/config";
import {
  type FamilySubPractice,
  practices,
} from "../v2/funnel/config/practices";
import { useFunnelAnswers } from "../v2/funnel/hooks/useFunnelAnswers";

import { calculateCost } from "./calculator/calculate-cost";
import { isStateUnsupported } from "./calculator/supported-states";
import { CalculatorAboutYou } from "./components/calculator-about-you";
import { CostCalculatorSection } from "./components/calculator-section";
import { CalculatorYourCase } from "./components/calculator-your-case";
import {
  CALCULATOR_PAGE_NAME,
  CALCULATOR_STEP_KEY,
  COST_CALCULATOR_STORAGE_KEY,
  getEstimationUrl,
  getUnsupportedStateUrl,
} from "./calculator.consts";
import {
  type CostCalculatorClickBiEvent,
  type CostCalculatorStorage,
  type CostCalculatorViewBiEvent,
} from "./calculator.types";

const { t } = i18n;

type CalculatorFormValues = {
  hasMinorChildren: string;
  hasUpcomingCourtEvent: string;
  isLookingToSettle: string;
  legalPapers: "Yes" | "No";
  ownPropertyTogether: string;
  personalStatus: "single" | "married";
  serviceType?: string;
  subPractice: FamilySubPractice;
  zipCode: string;
};

// TODO: align properties keys & values with funnelData
const calculatorFormSchema = yup.object({
  hasMinorChildren: yup
    .string()
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  personalStatus: yup
    .string()
    .oneOf(["single", "married"])
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  zipCode: yup
    .string()
    .trim()
    .label(t("COST_CALCULATOR_ZIP_CODE_INPUT_LABEL"))
    .required(t("COST_CALCULATOR_FORM_ZIP_CODE_INPUT_VALIDATION_ERROR"))
    .min(5)
    .max(5),
  subPractice: yup
    .string()
    .oneOf(practices.family)
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  serviceType: yup.string().when("subPractice", {
    is: (value: string) => value !== "else",
    then: (schema) =>
      schema.required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
    otherwise: (schema) => schema.optional(),
  }),
  legalPapers: yup
    .string()
    .oneOf(["Yes", "No"])
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  hasUpcomingCourtEvent: yup
    .string()
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  ownPropertyTogether: yup
    .string()
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
  isLookingToSettle: yup
    .string()
    .required(t("COST_CALCULATOR_OPTION_VALIDATION_ERROR")),
});

export function CostCalculatorForm() {
  const { funnelAnswers, setFunnelAnswers } = useFunnelAnswers();
  const router = useRouter();
  const { query } = router;

  const practice = query.page?.[0] as FunnelAnswers["practice"];

  const methods = useForm<CalculatorFormValues>({
    resolver: yupResolver(calculatorFormSchema),
  });

  const {
    formState: { isSubmitting },
    watch,
  } = methods;

  const [subPractice, serviceType, ...restAboutYouFields] = watch([
    "subPractice",
    "serviceType",
    "personalStatus",
    "hasMinorChildren",
  ]);
  const [zipCode, ...restYouCaseFields] = watch([
    "zipCode",
    "legalPapers",
    "hasUpcomingCourtEvent",
    "ownPropertyTogether",
    "isLookingToSettle",
  ]);

  function validateAboutYouFields() {
    const requiredFields = [subPractice, ...restAboutYouFields];
    if (subPractice !== "else" && serviceType) {
      requiredFields.push(serviceType);
    }
    return requiredFields.every(Boolean);
  }

  function validateYouCaseFields() {
    const zipCodeSchema = yup.string().required().min(5).max(5);
    const isZipCodeValid = zipCodeSchema.isValidSync(zipCode);
    return isZipCodeValid && restYouCaseFields.every(Boolean);
  }

  async function onSubmit(data: CalculatorFormValues) {
    const { state } = await getAddress(data.zipCode);
    const baseBiEventData = {
      page_name: CALCULATOR_PAGE_NAME,
      current_step_key: CALCULATOR_STEP_KEY,
      answer_value: Object.entries(data).flat().toString(),
      full_answers: JSON.stringify(data),
      click_text: t("COST_CALCULATOR_FORM_SUBMIT"),
      click_type: "submit" as const,
    };

    if (isStateUnsupported(state)) {
      const biEventData: CostCalculatorClickBiEvent = {
        ...baseBiEventData,
        next_step_key: "unsupported_state",
      };
      analytics.track(BiEventsNames.WebCostCalculatorFunnelError, biEventData);
      await router.push(getUnsupportedStateUrl(`${practice}`));
      return;
    }

    const { serviceType, ...restData } = data;
    const answers: CostCalculatorStorage["answers"] = {
      ...restData,
      practice,
      state,
    };

    if (serviceType) {
      answers.serviceType = serviceType;
    }

    const cost = calculateCost(answers);

    const costCalculator: CostCalculatorStorage = {
      url: router.asPath,
      answers,
      cost: cost || undefined,
    };

    setFunnelAnswers({
      ...funnelAnswers,
      ...answers,
    });

    setParsedItem(COST_CALCULATOR_STORAGE_KEY, costCalculator);
    const biEventData: CostCalculatorClickBiEvent = {
      ...baseBiEventData,
      cost,
      next_step_key: "estimation",
    };
    analytics.track(BiEventsNames.WebCostCalculatorClick, biEventData);
    await router.push(getEstimationUrl(`${practice}`));
  }

  useEffect(() => {
    const biEventData: CostCalculatorViewBiEvent = {
      page_name: CALCULATOR_PAGE_NAME,
      current_step_key: CALCULATOR_STEP_KEY,
    };
    analytics.track(BiEventsNames.WebCostCalculatorView, biEventData);
  }, []);

  return (
    <FormProvider {...methods}>
      <div className="w-full max-w-[606px]">
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <div className="mb-sp-4 flex flex-col gap-sp-3">
            <CostCalculatorSection
              isFilled={validateAboutYouFields()}
              title={t("COST_CALCULATOR_SECTION_TITLE_ABOUT_YOU")}
              imageSrc="/next/images/cost-calculator/about-section-icon.png"
            >
              <CalculatorAboutYou />
            </CostCalculatorSection>
            <CostCalculatorSection
              isFilled={validateYouCaseFields()}
              title={t("COST_CALCULATOR_SECTION_TITLE_YOUR_CASE")}
              imageSrc="/next/images/cost-calculator/case-section-icon.png"
            >
              <CalculatorYourCase />
            </CostCalculatorSection>
          </div>
          <Button size="lg" isLoading={isSubmitting} fullWidth type="submit">
            {t("COST_CALCULATOR_FORM_SUBMIT")}
          </Button>
        </form>
      </div>
    </FormProvider>
  );
}
